import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, Filter } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSetores } from "@/hooks/use-setores";
import { fetchJson } from "@/lib/queryClient";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import * as XLSX from "xlsx";
import type { Variedade } from "@shared/schema";
import {
  mesParcial,
  type CelulaRendimento,
  type DistribuicaoVariedade,
} from "@shared/rendimento";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { DataCard } from "@/components/data/data-card";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { useVariedadesDoCacho } from "@/hooks/use-variedades-cacho";
import { combina } from "@/lib/busca";
/**
 * Rendimento por Setor — o quinto relatório de /cacho/relatorios.
 *
 * ── POR QUE RELATÓRIO NOVO, E NÃO UMA ABA DO "PERFORMANCE POR SETOR" ──────────────────
 * Performance mede CACHOS (planejado × colhido × perda) no grão setor × ciclo, sobre TODA a
 * colheita. Este mede KG POR CACHO no grão setor × mês × variedade, e só sobre o cacho que
 * tem venda vinculada. Em junho a fazenda colheu 1.456 cachos e só 374 têm venda: um
 * "colhido: 1.456" ao lado de um "cachos: 374" na mesma tela seria lido como o mesmo
 * conjunto — a classe de erro do card Colhido, montada de propósito. Telas separadas, e esta
 * DECLARA a própria cobertura dentro dela.
 */

interface RespostaRendimento {
  celulas: CelulaRendimento[];
  distribuicao: DistribuicaoVariedade[];
  cobertura: {
    kgAlocado: number;
    kgVendido: number;
    cachosAlocados: number;
    cachosColhidos: number;
    mesesParciais: string[];
  };
}

type FiltroPeriodo = "ultimos_3_meses" | "ultimos_6_meses" | "ano_atual" | "personalizado";

const ROTULO_MES = [
  "jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez",
];

function rotuloMes(mes: string): string {
  const partes = mes.split("-");
  const indice = Number(partes[1]) - 1;
  return `${ROTULO_MES[indice] ?? mes}/${partes[0].slice(2)}`;
}

/** Soma dias de uma data civil, sem passar por fuso — `new Date()` aqui reintroduziria a
 *  virada de dia que o #148 fechou. */
function somarMeses(dataIso: string, meses: number): string {
  const [ano, mes, dia] = dataIso.split("-").map(Number);
  const total = (ano * 12 + (mes - 1)) + meses;
  const anoNovo = Math.floor(total / 12);
  const mesNovo = (total % 12) + 1;
  // Dia 31 em mês de 30 é irrelevante aqui (é limite de filtro, não data de registro), mas
  // grampear em 28 evitaria virar o mês. Mantemos o dia e deixamos o banco comparar string.
  const diaSeguro = Math.min(dia, 28);
  return `${String(anoNovo).padStart(4, "0")}-${String(mesNovo).padStart(2, "0")}-${String(diaSeguro).padStart(2, "0")}`;
}

const fmt = (n: number, casas = 2) =>
  n.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas });
const fmtInt = (n: number) => Math.round(n).toLocaleString("pt-BR");

/** Cor por percentil da PRÓPRIA variedade — uma régua por variedade, nunca uma só para as
 *  duas: a mediana da Nanica é maior que o máximo da Prata. */
function corDaCelula(kgPorCacho: number | null, dist: DistribuicaoVariedade | undefined): string {
  if (kgPorCacho === null || !dist) return "bg-muted/40";
  if (kgPorCacho < dist.p10) return "bg-erro text-background";
  if (kgPorCacho < dist.p25) return "bg-erro-suave text-erro";
  if (kgPorCacho > dist.p90) return "bg-sucesso text-background";
  if (kgPorCacho > dist.p75) return "bg-sucesso-suave text-sucesso";
  return "bg-muted/60";
}

function Selo({ tipo }: { tipo: "parcial" | "indistinto" | "amostra" }) {
  const estilo = {
    parcial: "bg-atencao-suave text-atencao",
    indistinto: "bg-info-suave text-info",
    amostra: "bg-muted text-muted-foreground",
  }[tipo];
  const texto = { parcial: "parcial", indistinto: "indistinto", amostra: "n<80" }[tipo];
  return (
    <span className={`inline-block text-[9px] font-extrabold uppercase tracking-wide rounded px-1 ml-1 align-middle ${estilo}`}>
      {texto}
    </span>
  );
}

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook).
const PADRAO_RENDIMENTO = {
  periodo: "ultimos_3_meses", de: "", ate: "", variedade: "todas", setor: "todos", q: "",
};
const PERMITIDOS_RENDIMENTO = {
  periodo: ["ultimos_3_meses", "ultimos_6_meses", "ano_atual", "personalizado"],
} as const;

export default function RelatoriosRendimentoSetorPage() {
  const { toast } = useToast();
  const hoje = hojeNaFazenda();

  const { estado: filtros, definir } = useEstadoNaUrl(PADRAO_RENDIMENTO, PERMITIDOS_RENDIMENTO);
  const filtroPeriodo = filtros.periodo as FiltroPeriodo;
  // Vazio = a janela padrão do preset, derivada — a data só vai para a URL
  // quando o Hugo a escolhe.
  const dataInicio = filtros.de || somarMeses(hoje, -3);
  const dataFim = filtros.ate || hoje;
  const variedadeFilter = filtros.variedade;
  const setorFilter = filtros.setor;

  const { de, ate } = useMemo(() => {
    if (filtroPeriodo === "personalizado") return { de: dataInicio, ate: dataFim };
    if (filtroPeriodo === "ultimos_6_meses") return { de: somarMeses(hoje, -6), ate: hoje };
    if (filtroPeriodo === "ano_atual") return { de: `${hoje.slice(0, 4)}-01-01`, ate: hoje };
    return { de: somarMeses(hoje, -3), ate: hoje };
  }, [filtroPeriodo, dataInicio, dataFim, hoje]);

  const { setores } = useSetores({ somenteProdutivos: true });
  const { variedades } = useVariedadesDoCacho();

  const params = new URLSearchParams({ de, ate });
  if (variedadeFilter !== "todas") params.set("variedadeId", variedadeFilter);
  if (setorFilter !== "todos") params.set("setorId", setorFilter);
  const url = `/api/cacho/rendimento?${params.toString()}`;

  const { data, isLoading } = useQuery<RespostaRendimento>({
    queryKey: ["/api/cacho/rendimento", de, ate, variedadeFilter, setorFilter],
    queryFn: () => fetchJson<RespostaRendimento>(url),
  });

  const celulas = data?.celulas ?? [];
  const mesesParciais = data?.cobertura.mesesParciais ?? [];

  /** Um bloco por variedade: cada uma tem a sua própria régua de percentis, e misturá-las
   *  numa matriz só faria uma célula de Nanica parecer excelente ao lado de uma de Prata. */
  const blocos = useMemo(() => {
    const porVariedade = new Map<string, CelulaRendimento[]>();
    for (const c of celulas) {
      const lista = porVariedade.get(c.variedadeId);
      if (lista) lista.push(c);
      else porVariedade.set(c.variedadeId, [c]);
    }
    return Array.from(porVariedade.entries()).map(([variedadeId, doTipo]) => {
      const setoresDoTipo = Array.from(new Set(doTipo.map(c => c.setorNome))).sort();
      const mesesDoTipo = Array.from(new Set(doTipo.map(c => c.mes))).sort();
      return {
        variedadeId,
        variedadeNome: doTipo[0].variedadeNome,
        celulas: doTipo,
        setores: setoresDoTipo,
        meses: mesesDoTipo,
        dist: data?.distribuicao.find(d => d.variedadeId === variedadeId),
      };
    }).sort((a, b) => b.celulas.length - a.celulas.length);
  }, [celulas, data]);

  // Busca NO CLIENTE, por setor: recorta os setores dentro de cada bloco de
  // variedade, e o bloco que ficou sem nenhum some — cabeçalho de variedade
  // sobre grade vazia não diz nada. A linha de COBERTURA acima continua
  // descrevendo o recorte inteiro, que é o universo do indicador.
  const busca = filtros.q;
  const blocosVisiveis = useMemo(() => {
    if (!busca.trim()) return blocos;
    return blocos
      .map(b => ({ ...b, setores: b.setores.filter(st => combina(busca, st)) }))
      .filter(b => b.setores.length > 0);
  }, [blocos, busca]);

  const handleExportExcel = () => {
    try {
      // Quem abre no Excel perde as cores e os selos. Estas três colunas são o que sobra da
      // honestidade da tela — em TEXTO legível, nunca booleano cru: sem elas a planilha vira
      // a versão confiável-e-errada do relatório.
      const dataToExport = celulas.map(c => ({
        "Setor": c.setorNome,
        "Mês": c.mes,
        "Variedade": c.variedadeNome,
        "kg": Number(c.kg.toFixed(2)),
        "Cachos": Number(c.cachos.toFixed(2)),
        "kg/cacho": c.kgPorCacho === null ? "—" : Number(c.kgPorCacho.toFixed(2)),
        "Dias": c.dias,
        "Vendas": c.vendas,
        "Parcial": mesesParciais.indexOf(c.mes) !== -1 ? "parcial" : "",
        "Indistinto": c.indistinto ? "indistinto" : "",
        "Amostra": c.amostraPequena ? "amostra pequena" : "",
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rendimento");
      ws["!cols"] = [12, 10, 14, 12, 10, 10, 8, 8, 12, 14, 18].map(w => ({ wch: w }));

      // `hojeNaFazenda()`, nunca `new Date().toISOString()` — o relatorios-performance-setor
      // usa o segundo (auditado no #148, lá é só nome de arquivo), mas não vamos plantar mais um.
      const filename = `rendimento_por_setor_${hojeNaFazenda()}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast({ title: "Exportado com sucesso", description: `Arquivo ${filename} foi baixado` });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast({ title: "Erro ao exportar", description: "Não foi possível gerar o arquivo", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Mesma anatomia dos outros quatro relatórios */}
      <div className="flex items-start gap-3">
        <Link href="/cacho/relatorios">
          <Button variant="ghost" size="icon" data-testid="button-voltar-relatorios">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <PageHeader
            titulo="Rendimento por setor"
            descricao="kg por cacho, por setor e mês"
            acao={
              <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={handleExportExcel} data-testid="button-export-excel">
                <Download className="h-4 w-4 mr-2" />
                Exportar
              </Button>
            }
          />
        </div>
      </div>

      {/* Toolbar do Índice: sem card, sem moldura, sem o rótulo "Filtros". */}
      <PageToolbar
        fim={
          <>
            <PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Setor" testId="input-busca-rendimento" />
            <span className="text-sm text-muted-foreground whitespace-nowrap" data-testid="texto-registros">
              {celulas.length} registros
            </span>
          </>
        }
      >
        <Label className="text-xs text-muted-foreground whitespace-nowrap">Período</Label>
        <Select value={filtroPeriodo} onValueChange={v => definir({ periodo: v })}>
          <SelectTrigger className="w-[190px] shrink-0" data-testid="select-periodo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ultimos_3_meses">Últimos 3 meses</SelectItem>
            <SelectItem value="ultimos_6_meses">Últimos 6 meses</SelectItem>
            <SelectItem value="ano_atual">Ano Atual</SelectItem>
            <SelectItem value="personalizado">Período Específico</SelectItem>
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Variedade</Label>
        <Select value={variedadeFilter} onValueChange={v => definir({ variedade: v })}>
          <SelectTrigger className="w-[190px] shrink-0" data-testid="select-variedade"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas as Variedades</SelectItem>
            {variedades?.slice().sort((a, b) => a.nome.localeCompare(b.nome)).map(v => (
              <SelectItem key={v.id} value={v.id}>{v.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Setor</Label>
        <Select value={setorFilter} onValueChange={v => definir({ setor: v })}>
          <SelectTrigger className="w-[180px] shrink-0" data-testid="select-setor"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os Setores</SelectItem>
            {setores?.filter(s => s.ativo).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map(s => (
              <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtroPeriodo === "personalizado" && (
          <>
            <Input type="date" value={dataInicio} onChange={e => definir({ de: e.target.value })} className="w-[150px] shrink-0 text-xs" data-testid="input-data-inicio" />
            <span className="text-xs text-muted-foreground">até</span>
            <Input type="date" value={dataFim} onChange={e => definir({ ate: e.target.value })} className="w-[150px] shrink-0 text-xs" data-testid="input-data-fim" />
          </>
        )}
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={4} />
      ) : celulas.length === 0 ? (
        <EmptyState
          titulo="Nenhuma colheita com venda vinculada"
          descricao="O relatório só enxerga o cacho que tem venda associada — associe as colheitas às vendas para que ele apareça aqui."
          testId="empty-rendimento"
        />
      ) : blocosVisiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          {/* ⚠️ A LINHA DE COBERTURA, OBRIGATÓRIA. Todo número que exclui parte do universo
              declara quanto excluiu, no mesmo lugar onde aparece. Foi cobertura implícita
              que produziu o bug do card Colhido. */}
          {data && (
            <DataCard padded testId="card-cobertura">
              <p className="text-sm">
                <span className="font-semibold">Base do indicador: </span>
                {fmtInt(data.cobertura.kgAlocado)} de {fmtInt(data.cobertura.kgVendido)} kg vendidos
                {" · "}
                {fmtInt(data.cobertura.cachosAlocados)} de {fmtInt(data.cobertura.cachosColhidos)} cachos colhidos
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Só entra aqui o cacho com venda vinculada. O kg vendido é datado pela venda e o
                cacho colhido pela colheita — são dois calendários, por isso os dois números
                aparecem inteiros em vez de virarem uma única porcentagem.
              </p>
            </DataCard>
          )}

          {blocosVisiveis.map(bloco => {
            const umSetorSo = bloco.setores.length === 1;
            return (
              <DataCard
                key={bloco.variedadeId}
                titulo={bloco.variedadeNome}
                meta={`${bloco.setores.length} setor(es)`}
                testId={`bloco-variedade-${bloco.variedadeId}`}
              >
                <div className="p-4 md:p-6 space-y-4">
                  {/* KPIs por mês */}
                  <div className="flex flex-wrap gap-2">
                    {bloco.meses.map(m => {
                      const doMes = bloco.celulas.filter(c => c.mes === m);
                      const kg = doMes.reduce((a, c) => a + c.kg, 0);
                      const cachos = doMes.reduce((a, c) => a + c.cachos, 0);
                      return (
                        <div key={m} className="flex-1 min-w-[130px] rounded-lg border bg-muted/30 px-3 py-2">
                          <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                            {rotuloMes(m)}
                            {mesParcial(m, hoje) && <Selo tipo="parcial" />}
                          </div>
                          <div className="text-lg font-extrabold">
                            {cachos > 0 ? `${fmt(kg / cachos)} kg/cacho` : "—"}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {fmtInt(kg)} kg · {fmtInt(cachos)} cachos
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Uma variedade num setor só não é comparação entre setores — dizer isso em
                      vez de desenhar uma matriz de uma linha com cara de ranking. */}
                  {umSetorSo ? (
                    <div className="rounded-lg border bg-muted/20 p-4 text-sm" data-testid="nota-setor-unico">
                      <p className="mb-2">
                        <b>{bloco.variedadeNome} tem um setor só ({bloco.setores[0]}) neste recorte.</b>{" "}
                        A comparação entre setores não se aplica — o valor aqui é temporal, não espacial.
                      </p>
                      <div className="flex flex-wrap items-center gap-2 font-mono font-bold">
                        {bloco.meses.map((m, i) => {
                          const c = bloco.celulas.filter(x => x.mes === m)[0];
                          return (
                            <span key={m}>
                              {i > 0 && <span className="text-muted-foreground mr-2">→</span>}
                              {c && c.kgPorCacho !== null ? fmt(c.kgPorCacho) : "—"}
                              {mesParcial(m, hoje) && <Selo tipo="parcial" />}
                              {c?.amostraPequena && <Selo tipo="amostra" />}
                              {c?.indistinto && <Selo tipo="indistinto" />}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Desktop: matriz setor × mês */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm border-separate border-spacing-y-1">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              <th className="text-left font-bold py-1">Setor</th>
                              {bloco.meses.map(m => (
                                <th key={m} className="text-right font-bold py-1 px-2">
                                  {rotuloMes(m)}{mesParcial(m, hoje) && <Selo tipo="parcial" />}
                                </th>
                              ))}
                              <th className="text-right font-bold py-1 px-2">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bloco.setores.map(setorNome => {
                              const doSetor = bloco.celulas.filter(c => c.setorNome === setorNome);
                              const ultimo = doSetor.filter(c => c.mes === bloco.meses[bloco.meses.length - 1])[0];
                              const penultimo = doSetor.filter(c => c.mes === bloco.meses[bloco.meses.length - 2])[0];
                              return (
                                <tr key={setorNome} data-testid={`linha-setor-${setorNome}`}>
                                  <td className="font-bold py-2">{setorNome}</td>
                                  {bloco.meses.map(m => {
                                    const c = doSetor.filter(x => x.mes === m)[0];
                                    if (!c) return <td key={m} className="text-right px-2 text-muted-foreground/40">—</td>;
                                    return (
                                      <td key={m} className={`text-right px-2 py-1.5 rounded-md ${corDaCelula(c.kgPorCacho, bloco.dist)}`}>
                                        <span className="font-extrabold tabular-nums">
                                          {c.kgPorCacho !== null ? fmt(c.kgPorCacho) : "—"}
                                        </span>
                                        {c.indistinto && <Selo tipo="indistinto" />}
                                        {c.amostraPequena && <Selo tipo="amostra" />}
                                        <span className="block text-[10px] opacity-75">
                                          {fmtInt(c.cachos)} cachos · {c.dias} dia{c.dias > 1 ? "s" : ""}
                                        </span>
                                      </td>
                                    );
                                  })}
                                  <td className="text-right px-2 text-xs font-bold">
                                    {ultimo?.kgPorCacho != null && penultimo?.kgPorCacho != null ? (
                                      (() => {
                                        const d = ((ultimo.kgPorCacho as number) - (penultimo.kgPorCacho as number)) / (penultimo.kgPorCacho as number) * 100;
                                        const cor = d > 3 ? "text-sucesso" : d < -3 ? "text-erro" : "text-muted-foreground";
                                        return (
                                          <span className={cor}>
                                            {d > 0 ? "+" : ""}{fmt(d, 0)}%
                                            {/* Δ contra mês parcial é DECLARADO, não escondido:
                                                o número não está errado, a base é que é outra. */}
                                            {mesesParciais.indexOf(bloco.meses[bloco.meses.length - 1]) !== -1 && (
                                              <span className="block text-[9px] font-normal text-muted-foreground">parcial</span>
                                            )}
                                          </span>
                                        );
                                      })()
                                    ) : <span className="text-muted-foreground/40">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile: um card por setor, os meses em linha — matriz de 12×3 não cabe
                          e rolagem horizontal no campo é pior que não ter o dado. */}
                      <div className="md:hidden space-y-2">
                        {bloco.setores.map(setorNome => {
                          const doSetor = bloco.celulas.filter(c => c.setorNome === setorNome);
                          return (
                            <div key={setorNome} className="rounded-lg border p-3" data-testid={`card-setor-${setorNome}`}>
                              <div className="font-bold text-sm mb-2">{setorNome}</div>
                              <div className="grid grid-cols-3 gap-2">
                                {bloco.meses.map(m => {
                                  const c = doSetor.filter(x => x.mes === m)[0];
                                  return (
                                    <div key={m} className={`rounded-md px-2 py-1.5 ${c ? corDaCelula(c.kgPorCacho, bloco.dist) : "bg-muted/20"}`}>
                                      <div className="text-[9px] uppercase font-bold opacity-70">{rotuloMes(m)}</div>
                                      <div className="font-extrabold tabular-nums text-sm">
                                        {c && c.kgPorCacho !== null ? fmt(c.kgPorCacho) : "—"}
                                      </div>
                                      {c && <div className="text-[9px] opacity-75">{fmtInt(c.cachos)} cachos</div>}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="mt-1">
                                {bloco.meses.map(m => {
                                  const c = doSetor.filter(x => x.mes === m)[0];
                                  if (!c || (!c.indistinto && !c.amostraPequena && !mesParcial(m, hoje))) return null;
                                  return (
                                    <span key={m} className="text-[9px] text-muted-foreground mr-2">
                                      {rotuloMes(m)}:
                                      {mesParcial(m, hoje) && <Selo tipo="parcial" />}
                                      {c.indistinto && <Selo tipo="indistinto" />}
                                      {c.amostraPequena && <Selo tipo="amostra" />}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* Legenda + o estado da banda aprendida */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1 border-t">
                    <span><i className="inline-block w-3 h-3 rounded-sm bg-erro align-[-2px] mr-1" />abaixo do p10</span>
                    <span><i className="inline-block w-3 h-3 rounded-sm bg-erro-suave border border-erro/30 align-[-2px] mr-1" />p10–p25</span>
                    <span><i className="inline-block w-3 h-3 rounded-sm bg-muted align-[-2px] mr-1 border" />normal</span>
                    <span><i className="inline-block w-3 h-3 rounded-sm bg-sucesso-suave border border-sucesso/30 align-[-2px] mr-1" />p75–p90</span>
                    <span><i className="inline-block w-3 h-3 rounded-sm bg-sucesso align-[-2px] mr-1" />acima do p90</span>
                  </div>

                  {bloco.dist && (
                    <p className="text-[11px] text-muted-foreground" data-testid={`banda-${bloco.variedadeId}`}>
                      {bloco.dist.aprendida ? (
                        <>
                          Faixa aprendida de <b>{bloco.dist.n} grupos</b> (dia × setor × variedade):
                          mediana <b>{fmt(bloco.dist.p50)}</b> · p10 {fmt(bloco.dist.p10)} · p90 {fmt(bloco.dist.p90)}.
                        </>
                      ) : (
                        <>
                          Só <b>{bloco.dist.n} grupos</b> nesta variedade — menos que o mínimo de 20 para
                          aprender uma faixa. O aviso de peso implausível segue usando a <b>faixa fixa
                          5–40 kg/cacho</b>, e as cores acima são apenas descritivas desta amostra.
                        </>
                      )}
                    </p>
                  )}
                </div>
              </DataCard>
            );
          })}

          {/* A limitação que o modelo tem e a tela precisa dizer. */}
          {celulas.some(c => c.indistinto) && (
            <DataCard className="border-info/30 bg-info-suave/50" padded testId="nota-indistinto">
              <div className="text-sm space-y-2">
                <p>
                  <b>Célula <Selo tipo="indistinto" /> não quer dizer que os setores rendem igual.</b>{" "}
                  Dentro de uma venda, todos os grupos da mesma variedade recebem o <b>mesmo</b> kg/cacho
                  por construção do rateio. Quando uma única venda cobre vários setores, os três aparecem
                  com o valor idêntico — e isso é o modelo <b>não conseguindo distinguir</b>, não uma medida.
                </p>
                <p className="text-muted-foreground">
                  O setor só ganha identidade quando vendas diferentes, com cestas diferentes, o tocam.
                  Células indistintas também ficam fora do cálculo dos percentis: um valor que é média de
                  outros não é observação independente.
                </p>
              </div>
            </DataCard>
          )}
        </>
      )}
    </div>
  );
}
